import React, { useMemo } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import type { Location } from "./locationTypes";
import useTranslation from "../../../../shared/hooks/useTranslation";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface LocationPickerProps {
  value: string | null;
  onChange: (locationId: string | null) => void;
  locations: Location[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
}

/* ------------------------------------------------------------------ */
/*  Flattened option for MUI Autocomplete                              */
/* ------------------------------------------------------------------ */

interface LocationOption {
  id: string;
  name: string;
  parentName: string | null;
  displayLabel: string;
  groupLabel: string;
  machineCount?: number;
  isParent: boolean;
}

/* ------------------------------------------------------------------ */
/*  Flatten hierarchical locations into grouped options                 */
/* ------------------------------------------------------------------ */

function flattenLocations(locations: Location[]): LocationOption[] {
  const options: LocationOption[] = [];

  for (const loc of locations) {
    if (loc.isDisabled) continue;

    if (loc.childLocations && loc.childLocations.length > 0) {
      // Parent with children: add each child grouped under the parent
      for (const child of loc.childLocations) {
        if (child.isDisabled) continue;
        options.push({
          id: child.id,
          name: child.name,
          parentName: loc.name,
          displayLabel: `${loc.name} > ${child.name}`,
          groupLabel: loc.name,
          machineCount: child.machineCount,
          isParent: false,
        });
      }
    } else {
      // Top-level location with no children: selectable on its own
      options.push({
        id: loc.id,
        name: loc.name,
        parentName: null,
        displayLabel: loc.name,
        groupLabel: loc.name,
        machineCount: loc.machineCount,
        isParent: true,
      });
    }
  }

  return options;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  locations,
  label,
  placeholder = "Select location...",
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  size = "small",
}) => {
  const { translate } = useTranslation("myMachinery");
  const options = useMemo(() => flattenLocations(locations), [locations]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value]
  );

  return (
    <Autocomplete<LocationOption, false, false, false>
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      options={options}
      groupBy={(option) => option.groupLabel}
      getOptionLabel={(option) => option.displayLabel}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      value={selectedOption}
      onChange={(_, selected) => onChange(selected?.id ?? null)}
      filterOptions={(opts, { inputValue }) => {
        if (!inputValue) return opts;
        const lower = inputValue.toLowerCase();
        return opts.filter(
          (o) =>
            o.name.toLowerCase().includes(lower) ||
            (o.parentName && o.parentName.toLowerCase().includes(lower))
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              py: "6px",
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 700,
              color: dt.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderBottom: `1px solid ${dt.rowBorder}`,
              position: "sticky",
              top: -8,
              backgroundColor: dt.headerBg,
              zIndex: 1,
            }}
          >
            <PlaceOutlinedIcon sx={{ fontSize: 14, color: dt.blue }} />
            {params.group}
          </Box>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: option.isParent ? "12px !important" : "12px !important",
            pl: option.isParent ? "12px !important" : "28px !important",
            py: "7px !important",
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textPrimary,
            cursor: "pointer",
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: `${dt.blueSoft} !important` },
            "&.Mui-focused": { backgroundColor: `${dt.blueSoft} !important` },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            <BusinessOutlinedIcon
              sx={{ fontSize: 14, color: dt.textMuted, flexShrink: 0 }}
            />
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option.name}
            </Box>
          </Box>
          {typeof option.machineCount === "number" && (
            <Box
              sx={{
                fontSize: "11px",
                fontFamily: dt.font,
                color: dt.textMuted,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {translate("locations.machineCount", { count: option.machineCount })}
            </Box>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <PlaceOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: selectedOption ? dt.blue : dt.textMuted,
                    ml: "2px",
                    mr: "-2px",
                    flexShrink: 0,
                  }}
                />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: dt.font,
              fontSize: "14px",
              borderRadius: dt.radiusSm,
              backgroundColor: dt.inputBg,
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: dt.cardBorder },
              "&.Mui-focused fieldset": {
                borderColor: dt.blue,
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              opacity: 0.55,
              fontFamily: dt.font,
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            borderRadius: dt.radiusSm,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: `1px solid ${dt.cardBorder}`,
            mt: "4px",
            "& .MuiAutocomplete-listbox": {
              padding: "4px 0",
              maxHeight: "280px",
            },
          },
        },
        clearIndicator: {
          sx: {
            fontSize: "18px",
            color: dt.textMuted,
            "&:hover": { color: dt.textPrimary },
          },
        },
      }}
    />
  );
};

export default LocationPicker;
